import { registerSchema } from '@nexo/types';
import { Button, Field, Input } from '@nexo/ui';
import { useState, type SubmitEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { errorMessage, errorsFromApi, useForm, validate } from '../../lib/forms';
import { useAuth } from './AuthProvider';
import { AuthLayout } from './AuthLayout';

export function RegisterPage() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const location = useLocation();
  const form = useForm({ displayName: '', email: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const { data, errors } = validate(registerSchema, form.values);
    if (errors) return form.setErrors(errors);
    setLoading(true);
    setError(null);
    try {
      await register(data);
    } catch (err) {
      const fieldErrors = errorsFromApi(err);
      if (fieldErrors) form.setErrors(fieldErrors);
      else setError(errorMessage(err));
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title={t('Crea tu cuenta')}
      subtitle={t('Empieza a colaborar con tu equipo en un minuto.')}
      footer={
        <>
          {t('¿Ya tienes cuenta?')}{' '}
          <Link to={`/login${location.search}`} className="font-medium text-accent hover:underline">
            {t('Inicia sesión')}
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field label={t('Nombre')} error={form.errors.displayName}>
          {(props) => (
            <Input
              {...props}
              autoComplete="name"
              autoFocus
              value={form.values.displayName}
              onChange={(e) => form.set('displayName', e.target.value)}
            />
          )}
        </Field>
        <Field label={t('Email')} error={form.errors.email}>
          {(props) => (
            <Input
              {...props}
              type="email"
              autoComplete="email"
              value={form.values.email}
              onChange={(e) => form.set('email', e.target.value)}
            />
          )}
        </Field>
        <Field label={t('Contraseña')} error={form.errors.password} hint={t('Mínimo 8 caracteres')}>
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              value={form.values.password}
              onChange={(e) => form.set('password', e.target.value)}
            />
          )}
        </Field>
        {error && (
          <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </p>
        )}
        <Button type="submit" size="lg" loading={loading} className="mt-2 w-full">
          {t('Crear cuenta')}
        </Button>
        <p className="text-center text-xs text-muted">
          <Trans
            t={t}
            i18nKey="Al crear una cuenta aceptas los <terms>Términos de uso</terms> y la <privacy>Política de privacidad</privacy>."
            components={{
              terms: <Link to="/terms" className="text-accent hover:underline" />,
              privacy: <Link to="/privacy" className="text-accent hover:underline" />,
            }}
          />
        </p>
      </form>
    </AuthLayout>
  );
}
